<template>
  <div class="relative flex-col items-center justify-center flex">
    <div v-if="isTurn" class="flex flex-col gap-y-1 md:gap-y-2 lg:gap-y-4">
      <p class="text-base md:text-lg lg:text-2xl xl:text-3xl font-bold tracking-tight text-center">Your turn</p>
      <div class="lg:mb-4 flex gap-x-4 md:gap-x-6 lg:gap-x-10">
        <BaseButton class="md:px-4 md:py-2.5 lg:px-6 lg:py-3.5" background-color="#1AA570" border-color="var(--color-cw-green-border-shadow)" id="step4" @click="takeAction('MAKE_WORD_SCREEN')">
          <p class="w-full !text-sm md:!text-base xl:!text-xl">Make a word</p>
        </BaseButton>
        <BaseButton
          class="md:px-4 md:py-2.5 lg:px-6 lg:py-3.5"
          background-color="var(--color-cw-olive)"
          border-color="var(--color-cw-green-border-shadow)"
          id="step5"
          @click="takeAction('TRADE_CARD_SELECTION_SCREEN')"
        >
          <p class="w-full !text-sm md:!text-base xl:!text-xl">Trade a card</p>
        </BaseButton>
      </div>
    </div>
    <template v-else>
      <div class="lg:mb-2 flex flex-col items-center lg:gap-y-2">
        <BaseAvatar
          :src="currentTurnPlayerProfile?.avatarUrl"
          class="!size-10 lg:!size-14 xl:!size-16"
          :show-timer-border="!!currentTurnPlayerProfile?.actionTimeout"
          :time-remaining="currentTurnPlayerProfile?.actionTimeout || 0"
        />
        <template v-if="playStatus == 'finalizing_trade'">
          <div class="mt-1 flex items-center">
            <p class="text-base md:text-lg lg:text-2xl xl:text-3xl font-bold tracking-normal">{{ currentTurnPlayerProfile?.username }} is {{ acceptedOffer ? 'answering a question' : 'trading' }}</p>
            <span class="dots text-base md:text-lg lg:text-2xl xl:text-3xl font-bold tracking-normal" />
          </div>
          <a href="" @click.prevent="opponentQuestionModalRef?.open" class="inline-flex items-center mt-2 text-xl font-semibold text-cw-green-lighter" v-if="acceptedOffer">
            View question
            <ArrowSlideRightIcon class="w-auto h-4 ml-2" />
          </a>
        </template>
        <div v-else-if="playStatus == 'offering_trade'" class="mt-1 flex items-center">
          <p class="text-base md:text-lg lg:text-2xl xl:text-3xl font-bold tracking-normal">{{ currentTurnPlayerProfile?.username }} is trading</p>
          <span class="dots text-base md:text-lg lg:text-2xl xl:text-3xl font-bold tracking-normal" />
        </div>
        <div v-else class="mt-1 flex items-center">
          <p class="text-base md:text-lg lg:text-2xl xl:text-3xl font-bold tracking-normal">{{ currentTurnPlayerProfile?.username }} is playing</p>
          <span class="dots text-base md:text-lg lg:text-2xl xl:text-3xl font-bold tracking-normal" />
        </div>
      </div>
    </template>
    <OpponentQuestionModal v-if="playStatus == 'finalizing_trade'" ref="opponentQuestionModalRef" />
  </div>
</template>

<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import BaseAvatar from '@/components/base/BaseAvatar.vue'
import ArrowSlideRightIcon from '@/assets/svgs/icons/arrow-slide-right.svg'
import { useGameStore } from '@/stores/game'
import { storeToRefs } from 'pinia'
import { useGameArea } from '@/composables/useGameArea'
import OpponentQuestionModal from '@/components/modals/game-play/OpponentQuestionModal.vue'
import { computed, useTemplateRef } from 'vue'

const { setActiveGameAreaScreen, canCloseTradeCardOptions } = useGameArea()
const opponentQuestionModalRef = useTemplateRef('opponentQuestionModalRef')

const { isTurn, currentTurnPlayerProfile, playStatus, gameState } = storeToRefs(useGameStore())

const acceptedOffer = computed(() => {
  if (!gameState.value?.activeTrade) return null
  const offerIndex = gameState.value?.activeTrade?.selectedOfferIndex as number
  if (offerIndex === undefined || offerIndex < 0) return null
  return gameState.value!.activeTrade!.offers[offerIndex]
})

const takeAction = (action: 'MAKE_WORD_SCREEN' | 'TRADE_CARD_SELECTION_SCREEN') => {
  canCloseTradeCardOptions.value = true
  setActiveGameAreaScreen(action)
}
</script>

<style scoped></style>
